import * as React from 'react'
import { useUserAuthQuery } from 'auth/hooks'
import type {
  EventJson,
  NotificationPayloadJson,
} from '../gen/creativefabrica/notifications/v1/types_pb'
import { subscribeToEvent } from './notifications'

export type NotificationPayload = NotificationPayloadJson

interface UseNotificationsOptions {
  event?: EventJson
  onNotification?: (notification: NotificationPayloadJson) => void
  enabled?: boolean
}

export function useNotifications({
  event,
  onNotification,
  enabled = true,
}: UseNotificationsOptions) {
  const [currentNotification, setCurrentNotification] = React.useState<
    NotificationPayloadJson | undefined
  >(undefined)
  const hasBeenAccessedRef = React.useRef(false)
  const onNotificationRef = React.useRef<typeof onNotification>(onNotification)
  const { data: token } = useUserAuthQuery(undefined, {
    select: data => data?.me?.token,
  })

  React.useEffect(() => {
    onNotificationRef.current = onNotification
  }, [onNotification])

  React.useEffect(() => {
    if (!event || !token || !enabled) {
      return
    }

    return subscribeToEvent(
      event,
      notification => {
        onNotificationRef.current?.(notification)
        if (hasBeenAccessedRef.current) {
          setCurrentNotification(notification)
        }
      },
      token,
    )
  }, [event, token, enabled])

  // we can smortly see if the dev/user has accessed `.notification`,
  // if they have, we can then update the state. otherwise we don't need to.
  // this saves us from rerendering the component when the notification is updated if it's not used!
  const returnValue = React.useMemo(() => {
    return new Proxy<{ notification: NotificationPayloadJson | undefined }>(
      { notification: undefined },
      {
        get(target, prop) {
          if (prop === 'notification') {
            hasBeenAccessedRef.current = true
            return currentNotification
          }
          return target[prop as keyof typeof target]
        },
      },
    )
  }, [currentNotification])

  return returnValue
}
